import React from 'react';
import {
	Box,
	IconButton,
	Modal,
	Pagination,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { styled } from '@mui/system';
import { DistributionEvent } from 'types/distribution';
import moment from 'moment';
import { DATE_TIME_FORMAT, TOKEN } from 'data/constant';

interface Props {
	open: boolean;
	onClose: () => void;
	listDistribution: DistributionEvent[];
}

const StyledModalBox = styled(Box)({
	position: 'absolute',
	top: '50%',
	left: '50%',
	transform: 'translate(-50%, -50%)',
	width: 700,
	maxHeight: '80vh',
	backgroundColor: '#fff',
	borderRadius: 8,
	boxShadow: '0 4px 12px rgba(0,0,0,0.16)',
	outline: 'none',
	display: 'flex',
	flexDirection: 'column',
	padding: 24,
});

const StyleTableCell = styled(TableCell)`
	color: #8a8c92;
	background-color: #f3f3f3;
	border-top: 1px solid #cccccc;
	font-size: 12px;
	padding: 10px 16px;
`;

const Row = styled('div')`
	display: flex;
	flex-direction: row;
	margin-bottom: 10px;
`;

const StyleTableItem = styled(TableCell)`
	color: #444;
	border-top: 1px solid #cccccc;
	font-size: 14px;
	padding: 16px;
	align-items: start;
`;

const Header = styled(Box)({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
	marginBottom: 16,
});

const ScrollContainer = styled(TableContainer)({
	flexGrow: 1,
});

export default function DistributionHistoryModal({
	open,
	onClose,
	listDistribution,
}: Props) {
	const [page, setPage] = React.useState(1);
	const rowsPerPage = 3;

	const handlePageChange = (_: React.ChangeEvent<unknown>, value: number) => {
		setPage(value);
	};

	const paginated = React.useMemo(
		() =>
			listDistribution.slice(
				(page - 1) * rowsPerPage,
				page * rowsPerPage,
			),
		[listDistribution, page],
	);

	return (
		<Modal open={open} onClose={onClose} style={{ zIndex: 4000 }}>
			<StyledModalBox>
				<Header>
					<Typography variant="subtitle1" fontFamily={'AtAmiga'}>
						{`Distribution History`.toUpperCase()}
					</Typography>
					<IconButton size="small" onClick={onClose}>
						<CloseIcon fontSize="small" />
					</IconButton>
				</Header>

				{paginated.length === 0 && (
					<Typography variant="subtitle2">
						No Distribution yet
					</Typography>
				)}

				{paginated.length > 0 && (
					<>
						<ScrollContainer>
							<Table stickyHeader>
								<TableHead>
									<TableRow>
										<StyleTableCell>
											Distribution Date
										</StyleTableCell>
										<StyleTableCell>Point</StyleTableCell>
										<StyleTableCell>Reward</StyleTableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{paginated.map((row, idx) => (
										<TableRow
											key={idx}
											style={{
												verticalAlign: 'baseline',
											}}
										>
											<StyleTableItem>
												{moment(row.createdAt).format(
													DATE_TIME_FORMAT,
												)}
											</StyleTableItem>
											<StyleTableItem>
												{row.distribution.points}
											</StyleTableItem>
											<StyleTableItem>
												{row.distribution.tokens.map(
													(d, i) => (
														<Row key={i}>
															<img
																src={
																	TOKEN[
																		d
																			.tokenName
																	].url
																}
																width={18}
																height={18}
																alt={
																	TOKEN[
																		d
																			.tokenName
																	].name
																}
															/>
															<Box width={10} />
															<Typography
																variant={
																	'caption'
																}
															>
																{d.creditAmount}{' '}
																{d.tokenName}
															</Typography>
														</Row>
													),
												)}
											</StyleTableItem>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</ScrollContainer>

						<Box
							display="flex"
							justifyContent="center"
							marginTop={2}
						>
							<Pagination
								count={Math.ceil(
									listDistribution.length / rowsPerPage,
								)}
								page={page}
								onChange={handlePageChange}
								sx={{
									'& .MuiPaginationItem-root': {
										color: '#275FF1',
									},
									'& .MuiPaginationItem-root.Mui-selected': {
										backgroundColor: '#275FF1',
										color: '#ffffff',
									},
									'& .MuiPaginationItem-root.Mui-selected:hover':
										{
											backgroundColor: '#1f4ec1',
										},
								}}
							/>
						</Box>
					</>
				)}
			</StyledModalBox>
		</Modal>
	);
}
